import {
  PublicDedicatedCalendar,
  AggregatedSlot,
  BorderRadius,
  FontFamily,
  ThemeMode,
  BackgroundPattern,
  ShadowIntensity,
  ContainerWidth,
  SectionSpacing,
} from '@frontend/public-calendar';

/**
 * Preview data interface for embedding the dedicated calendar in preview mode.
 * This allows the Page Editor to show a live preview without fetching from API.
 */
export interface PreviewCalendarData {
  /** Provider info */
  providerId: string;
  companyName: string;
  logoUrl: string | null;

  /** Theme settings */
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  surfaceColor: string;
  textColor: string;
  textMutedColor: string;
  borderRadius: BorderRadius;
  fontFamily: FontFamily;
  themeMode: ThemeMode;
  backgroundPattern: BackgroundPattern;

  /** Extended theme settings */
  buttonRadius: BorderRadius;
  shadowIntensity: ShadowIntensity;
  containerWidth: ContainerWidth;
  sectionSpacing: SectionSpacing;
  linkColor: string;
  linkHoverColor: string;
  buttonTextColor: string;
  borderColor: string;

  /** Mock slots for preview */
  mockSlots?: AggregatedSlot[];

  /** Page sections for WYSIWYG preview */
  sections?: PreviewSection[];
}

/**
 * Section data for preview mode.
 */
export interface PreviewSection {
  id: string;
  type: string;
  displayOrder: number;
  isLocked: boolean;
  settings: Record<string, unknown>;
  content: Record<string, unknown>;
}

/**
 * Creates a PreviewCalendarData from a PublicDedicatedCalendar.
 */
export function toPreviewData(calendar: PublicDedicatedCalendar): PreviewCalendarData {
  return {
    providerId: calendar.providerId,
    companyName: calendar.companyName,
    logoUrl: calendar.logoUrl,
    primaryColor: calendar.primaryColor,
    secondaryColor: calendar.secondaryColor,
    accentColor: calendar.accentColor,
    backgroundColor: calendar.backgroundColor,
    surfaceColor: calendar.surfaceColor,
    textColor: calendar.textColor,
    textMutedColor: calendar.textMutedColor,
    borderRadius: calendar.borderRadius,
    fontFamily: calendar.fontFamily,
    themeMode: calendar.themeMode,
    backgroundPattern: calendar.backgroundPattern,
    buttonRadius: calendar.buttonRadius,
    shadowIntensity: calendar.shadowIntensity,
    containerWidth: calendar.containerWidth,
    sectionSpacing: calendar.sectionSpacing,
    linkColor: calendar.linkColor,
    linkHoverColor: calendar.linkHoverColor,
    buttonTextColor: calendar.buttonTextColor,
    borderColor: calendar.borderColor,
    sections: calendar.sections?.map(s => ({
      id: s.name,
      type: s.sectionType,
      displayOrder: s.sortOrder,
      isLocked: false,
      settings: s.settings,
      content: s.contentDe, // Default to German content
    })),
  };
}

/**
 * Creates a PublicDedicatedCalendar-like object from PreviewCalendarData.
 * Used internally by the preview component to work with existing theme service.
 */
export function toPublicCalendar(preview: PreviewCalendarData): PublicDedicatedCalendar {
  return {
    providerId: preview.providerId,
    companyName: preview.companyName,
    logoUrl: preview.logoUrl,
    primaryColor: preview.primaryColor,
    secondaryColor: preview.secondaryColor,
    accentColor: preview.accentColor,
    backgroundColor: preview.backgroundColor,
    surfaceColor: preview.surfaceColor,
    textColor: preview.textColor,
    textMutedColor: preview.textMutedColor,
    borderRadius: preview.borderRadius,
    fontFamily: preview.fontFamily,
    themeMode: preview.themeMode,
    backgroundPattern: preview.backgroundPattern,
    buttonRadius: preview.buttonRadius,
    shadowIntensity: preview.shadowIntensity,
    containerWidth: preview.containerWidth,
    sectionSpacing: preview.sectionSpacing,
    linkColor: preview.linkColor,
    linkHoverColor: preview.linkHoverColor,
    buttonTextColor: preview.buttonTextColor,
    borderColor: preview.borderColor,
    // Legal pages default to None for preview
    imprintType: 'None',
    imprintExternalUrl: null,
    imprintContentDe: null,
    imprintContentEn: null,
    privacyType: 'None',
    privacyExternalUrl: null,
    privacyContentDe: null,
    privacyContentEn: null,
    // Offer page defaults for preview
    offerPageSettings: null,
    offerPageContentDe: null,
    offerPageContentEn: null,
    // Map preview sections to public format
    sections: preview.sections?.map(s => ({
      sectionType: s.type,
      name: s.id,
      sortOrder: s.displayOrder,
      settings: s.settings,
      contentDe: s.content,
      contentEn: s.content,
    })) ?? [],
    calendars: [],
  };
}

/**
 * Default preview data with sensible defaults for design-first workflow.
 */
export function createDefaultPreviewData(companyName = 'Your Company'): PreviewCalendarData {
  return {
    providerId: 'preview',
    companyName,
    logoUrl: null,
    primaryColor: '#3B82F6',
    secondaryColor: '#1E40AF',
    accentColor: '#F59E0B',
    backgroundColor: '#FFFFFF',
    surfaceColor: '#F9FAFB',
    textColor: '#111827',
    textMutedColor: '#6B7280',
    borderRadius: 'md',
    fontFamily: 'system',
    themeMode: 'light',
    backgroundPattern: 'none',
    buttonRadius: 'md',
    shadowIntensity: 'md',
    containerWidth: 'default',
    sectionSpacing: 'default',
    linkColor: '#3B82F6',
    linkHoverColor: '#1E40AF',
    buttonTextColor: '#FFFFFF',
    borderColor: '#E5E7EB',
    sections: [],
  };
}

/**
 * Generates mock slots for preview mode.
 * Creates realistic-looking time slots for the current week.
 */
export function generateMockSlots(): AggregatedSlot[] {
  const slots: AggregatedSlot[] = [];
  const today = new Date();

  // Generate slots for the next 7 days
  for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
    const date = new Date(today);
    date.setDate(today.getDate() + dayOffset);

    // Skip weekends for more realistic preview
    if (date.getDay() === 0 || date.getDay() === 6) continue;

    const dateStr = date.toISOString().split('T')[0];

    // Morning slots
    slots.push({
      date: dateStr,
      startTime: '09:00',
      endTime: '10:00',
      availableCalendarIds: ['preview-cal-1'],
    });
    slots.push({
      date: dateStr,
      startTime: '10:00',
      endTime: '11:00',
      availableCalendarIds: ['preview-cal-1'],
    });
    slots.push({
      date: dateStr,
      startTime: '11:00',
      endTime: '12:00',
      availableCalendarIds: ['preview-cal-1'],
    });

    // Afternoon slots
    slots.push({
      date: dateStr,
      startTime: '14:00',
      endTime: '15:00',
      availableCalendarIds: ['preview-cal-1'],
    });
    slots.push({
      date: dateStr,
      startTime: '15:00',
      endTime: '16:00',
      availableCalendarIds: ['preview-cal-1'],
    });
    slots.push({
      date: dateStr,
      startTime: '16:00',
      endTime: '17:00',
      availableCalendarIds: ['preview-cal-1'],
    });
  }

  return slots;
}
